import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/store/storeCart";

type OrderRow = {
  id: string;
  order_number: string;
  status: string;
  total_cents: number;
  created_at: string;
  vendor: { display_name: string; slug: string } | null;
};

const StatusBadge = ({ status }: { status: string }) => {
  const variant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    pending: "secondary",
    paid: "default",
    fulfilled: "default",
    shipped: "default",
    delivered: "default",
    cancelled: "outline",
    refunded: "outline",
    partially_refunded: "outline",
    disputed: "destructive",
  };
  return <Badge variant={variant[status] ?? "secondary"}>{status.replace("_", " ")}</Badge>;
};

const StoreOrdersPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("store_orders")
        .select(
          "id, order_number, status, total_cents, created_at, vendor:store_vendors_public!vendor_id(display_name, slug)",
        )
        .eq("buyer_id", user.id)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setOrders((data as unknown as OrderRow[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!authLoading && !user) {
    return <Navigate to="/auth?redirect=/store/orders" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-4xl px-6 py-8">
        <h1 className="text-3xl font-bold">Your orders</h1>

        <div className="mt-6 space-y-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)
          ) : orders.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">You haven't placed any orders yet.</p>
              <Link to="/store" className="inline-block mt-4 underline text-sm">
                Browse the Store
              </Link>
            </Card>
          ) : (
            orders.map((o) => (
              <Link key={o.id} to={`/store/orders/${o.id}`}>
                <Card className="p-4 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{o.order_number}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {o.vendor?.display_name ?? "Vendor"} ·{" "}
                        {new Date(o.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold">{formatPrice(o.total_cents)}</p>
                      <StatusBadge status={o.status} />
                    </div>
                  </div>
                </Card>
              </Link>
            ))
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreOrdersPage;
